import type { ViewRData } from "../../types/unity";

/**
 * STUDIO-PACKAGE-001 「이미지 자산 정책」 — Revision 저장의 이미지 ref를 React가 조립한다.
 *
 * 생성 시점에 Project Asset으로 commit해 둔 ref(committed memo)와 마지막 Revision의 ref로
 * 저장 ref를 만들고, React가 source를 못 찾은 key만 Unity가 같은 key로 준 bytes에 맡긴다.
 *
 * ② HMI plane: index key의 bytes는 Unity가 슬롯별로 해석해 준다(scene capture·로드 bundle·세션 캐시).
 *   Unity bytes가 있으면 그것을 쓰고(같은 hash는 서버가 재업로드하지 않음), 없을 때만 React가 source를
 *   해석한다. 이전 저장의 bundle key면 마지막 Revision의 그 key ref를 현재 index key로 옮기고(Remove compaction),
 *   http(s) URL이면 bytes를 Project Asset으로 등록한다.
 * ④ Comment 첨부(`comment_*`)·⑤ 프로필(`profile_*`): React가 staging 즉시 commit한 memo → 마지막 Revision ref.
 * ③ XR Entry 썸네일(`xr_entry_*`)·⑥ Comment 캡처(`capture_*`): Unity가 렌더한 bytes를 React가 받는 즉시 commit한다.
 *   key가 같아도 재캡처(Update to current view·pin 이동)마다 bytes가 바뀌므로, 재캡처가 시작되면 그 key를 stale로
 *   표시해 새 commit 전까지 memo·이전 Revision ref를 쓰지 않고 Unity bytes에 맡긴다.
 */
export interface RevisionAssetRefLike {
  source: "project_asset" | "library_object";
  asset_id: string;
  logical_key: string;
}

export const PROJECT_THUMBNAIL_KEY = "thumbnail.jpeg";
export const FIGMA_HMI_BUNDLE_KEY_RE = /^figma_hmi_(\d+)_(\d+)_(\d+)\.png$/;

export const figmaHmiBundleKey = (
  spaceIndex: number,
  vseIndex: number,
  hmiIndex: number,
): string => `figma_hmi_${spaceIndex}_${vseIndex}_${hmiIndex}.png`;

// 프로젝트별 마지막으로 알려진 Revision asset ref (HEAD 로드·저장 성공 시 갱신).
const refsByProject = new Map<string, RevisionAssetRefLike[]>();
// 같은 Revision에서 HMI id → 그 HMI의 bundle key. index가 바뀌어도 id로 이전 이미지를 찾는다.
const hmiKeysByProject = new Map<string, Map<string, string>>();

export function rememberRevisionAssetRefs(
  projectId: string,
  refs: ReadonlyArray<RevisionAssetRefLike>,
  viewRData?: ViewRData,
): void {
  refsByProject.set(
    projectId,
    refs.map(({ source, asset_id, logical_key }) => ({ source, asset_id, logical_key })),
  );
  const keysById = new Map<string, string>();
  (viewRData?.spaces ?? []).forEach((space, s) =>
    (space?.vseList ?? []).forEach((vse, v) =>
      (vse?.vseFunction?.vseHmiInfo?.hmiIds ?? []).forEach((id, h) => {
        if (id?.trim()) keysById.set(id.trim(), figmaHmiBundleKey(s, v, h));
      }),
    ),
  );
  hmiKeysByProject.set(projectId, keysById);
}

export function getRevisionAssetRefs(projectId: string): RevisionAssetRefLike[] {
  return refsByProject.get(projectId) ?? [];
}

export function forgetRevisionAssetRefs(projectId?: string): void {
  if (projectId === undefined) {
    refsByProject.clear();
    hmiKeysByProject.clear();
  } else {
    refsByProject.delete(projectId);
    hmiKeysByProject.delete(projectId);
  }
}

// 현재 Editor 세션의 Revision 프로젝트. staging 시점 commit(④⑤, 이후 ①③⑥)이 대상 프로젝트를 알기 위한 값이며
// 세션 종료·legacy 프로젝트에서는 null이다.
let activeRevisionProjectId: string | null = null;

export function setActiveRevisionProject(projectId: string | null): void {
  activeRevisionProjectId = projectId && projectId.trim() ? projectId : null;
}

export function getActiveRevisionProject(): string | null {
  return activeRevisionProjectId;
}

// 생성 시점에 commit해 둔 이미지 asset: projectId → logical_key → ref
const committedByProject = new Map<string, Map<string, RevisionAssetRefLike>>();

// 재캡처가 시작돼 아직 새 bytes가 commit되지 않은 key: projectId → key
const staleByProject = new Map<string, Set<string>>();
// 생성 시점 commit이 아직 진행 중인 key. 저장이 겹치면 결과를 기다려 필수 누락으로 오판하지 않는다.
const pendingByProject = new Map<string, Map<string, Promise<unknown>>>();

export function rememberPendingImageAsset(
  projectId: string,
  logicalKey: string,
  work: Promise<unknown>,
): void {
  let byKey = pendingByProject.get(projectId);
  if (!byKey) {
    byKey = new Map();
    pendingByProject.set(projectId, byKey);
  }
  byKey.set(logicalKey, work);
  void work.finally(() => {
    if (pendingByProject.get(projectId)?.get(logicalKey) === work) {
      pendingByProject.get(projectId)?.delete(logicalKey);
    }
  }).catch(() => undefined);
}

export function getPendingImageAsset(projectId: string, logicalKey: string): Promise<unknown> | null {
  return pendingByProject.get(projectId)?.get(logicalKey) ?? null;
}

export function rememberCommittedImageAsset(
  projectId: string,
  logicalKey: string,
  ref: RevisionAssetRefLike,
): void {
  let byKey = committedByProject.get(projectId);
  if (!byKey) {
    byKey = new Map();
    committedByProject.set(projectId, byKey);
  }
  byKey.set(logicalKey, { source: ref.source, asset_id: ref.asset_id, logical_key: logicalKey });
  staleByProject.get(projectId)?.delete(logicalKey);
}

export function getCommittedImageAsset(
  projectId: string,
  logicalKey: string,
): RevisionAssetRefLike | null {
  if (isImageAssetStale(projectId, logicalKey)) return null;
  return committedByProject.get(projectId)?.get(logicalKey) ?? null;
}

/** 같은 key로 새 bytes가 렌더되는 중이다. 새 commit 전까지 memo·이전 Revision ref를 쓰지 않는다. */
export function markImageAssetStale(projectId: string, logicalKey: string): void {
  let keys = staleByProject.get(projectId);
  if (!keys) {
    keys = new Set();
    staleByProject.set(projectId, keys);
  }
  keys.add(logicalKey);
}

export function isImageAssetStale(projectId: string, logicalKey: string): boolean {
  return staleByProject.get(projectId)?.has(logicalKey) ?? false;
}

export function forgetCommittedImageAssets(projectId?: string): void {
  if (projectId === undefined) {
    committedByProject.clear();
    staleByProject.clear();
    pendingByProject.clear();
  } else {
    committedByProject.delete(projectId);
    staleByProject.delete(projectId);
    pendingByProject.delete(projectId);
  }
}

export const COMMENT_ATTACHMENT_KEY_RE = /^comment_[0-9a-f]{32}_[0-9a-f]{32}\.[A-Za-z0-9_-]+$/;
export const COMMENT_PROFILE_KEY_RE = /^profile_[0-9a-f]{64}\.[A-Za-z0-9_-]+$/;
export const COMMENT_CAPTURE_KEY_RE = /^capture_[0-9a-f]{32}\.[A-Za-z0-9_-]+$/;
export const XR_ENTRY_KEY_RE = /^xr_entry_[A-Za-z0-9-]+\.png$/;

export const commentCaptureKey = (commentId: string): string => `capture_${commentId}.jpg`;

/** Unity `XrEntryPointPreviewSupport.ThumbnailBundleKey`와 같은 규칙. */
export function xrEntryThumbnailKey(entryPointId: string | null | undefined): string | null {
  const normalized = entryPointId?.trim() ?? "";
  return /^[A-Za-z0-9-]+$/.test(normalized) ? `xr_entry_${normalized}.png` : null;
}

/** Unity `StudioVdbImageCollector.EnumerateXrEntryPointThumbnailKeys`와 같은 순서·중복 제거. */
export function enumerateXrEntryThumbnailKeys(viewRData: ViewRData): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const space of viewRData.spaces ?? []) {
    const entries = (space as { xrEntryPoints?: Array<{ entryPointId?: string } | null> })
      ?.xrEntryPoints;
    for (const entry of entries ?? []) {
      const key = xrEntryThumbnailKey(entry?.entryPointId);
      if (key && !seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys;
}

/** Unity `StudioVdbImageCollector.EnumerateCommentImageKeys`와 같은 순서·중복 제거. */
export function enumerateCommentImageKeys(viewRData: ViewRData): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const add = (key: string | undefined) => {
    const value = key?.trim();
    if (value && !seen.has(value)) {
      seen.add(value);
      keys.push(value);
    }
  };
  for (const space of viewRData.spaces ?? []) {
    for (const comment of space?.commentList ?? []) {
      if (!comment) continue;
      add(comment.authorProfileImageKey);
      add(comment.captureImageKey);
      for (const element of comment.elementList ?? []) {
        if (!element) continue;
        add(element.authorProfileImageKey);
        for (const key of element.imageList ?? []) add(key);
      }
    }
  }
  return keys;
}

export interface ImageAssetRefPlan {
  /** React가 확정한 ref. 같은 key의 Unity 이미지는 업로드하지 않는다. */
  refs: RevisionAssetRefLike[];
  /** React가 source를 못 찾아 Unity 이미지에 맡긴 key. */
  unityKeys: string[];
  /** 어느 쪽에도 source가 없는 key. */
  missing: string[];
  /**
   * Unity가 뒤따라 보내기로 했지만 오지 않은 HMI key. 이전 Revision ref로 채우지 않고(옛 이미지 방지)
   * 이미지 없이 저장한다. 다음 저장에서 Unity가 적용된 plane bytes를 준다.
   */
  deferred: string[];
}

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

export async function buildImageAssetRefs(args: {
  projectId: string;
  viewRData: ViewRData;
  unityImageKeys: ReadonlySet<string>;
  /** 요청 프레임 snapshot에서 끝내 오지 않은 이미지 key(`CompleteEditSnapshot.pendingImageKeys`). */
  pendingImageKeys?: ReadonlySet<string>;
  ensureAsset: (
    projectId: string,
    sourceUrl: string,
  ) => Promise<RevisionAssetRefLike | null>;
}): Promise<ImageAssetRefPlan> {
  const plan: ImageAssetRefPlan = { refs: [], unityKeys: [], missing: [], deferred: [] };
  const pendingKeys = args.pendingImageKeys ?? new Set<string>();
  const headRefs = getRevisionAssetRefs(args.projectId);
  const headByKey = new Map(
    headRefs
      .filter((ref) => ref.source === "project_asset")
      .map((ref) => [ref.logical_key, ref] as const),
  );
  const headHmiKeyById = hmiKeysByProject.get(args.projectId);
  const fallback = (key: string) => {
    if (args.unityImageKeys.has(key)) plan.unityKeys.push(key);
    else plan.missing.push(key);
  };

  // ③④⑤⑥: 생성·렌더 즉시 commit한 memo → (재캡처 중이 아니면) 마지막 Revision ref → Unity bytes
  for (const key of [
    ...enumerateCommentImageKeys(args.viewRData),
    ...enumerateXrEntryThumbnailKeys(args.viewRData),
  ]) {
    const inflight = getPendingImageAsset(args.projectId, key);
    if (inflight) await inflight.catch(() => undefined);
    const committed = getCommittedImageAsset(args.projectId, key);
    if (committed) {
      plan.refs.push(committed);
      continue;
    }
    const head = isImageAssetStale(args.projectId, key) ? undefined : headByKey.get(key);
    if (head) {
      plan.refs.push({ ...head });
      continue;
    }
    fallback(key);
  }

  // ② HMI: 슬롯별 source 해석
  const spaces = args.viewRData.spaces ?? [];
  for (let s = 0; s < spaces.length; s += 1) {
    const vseList = spaces[s]?.vseList ?? [];
    for (let v = 0; v < vseList.length; v += 1) {
      const hmi = vseList[v]?.vseFunction?.vseHmiInfo;
      if (!hmi) continue;
      const loadUrls = hmi.loadUrl ?? [];
      const thumbs = hmi.hmiPreviewThumbnailUrl ?? [];
      const ids = hmi.hmiIds ?? [];
      const slotCount = Math.max(loadUrls.length, thumbs.length);
      for (let h = 0; h < slotCount; h += 1) {
        const source = thumbs[h]?.trim() ?? "";
        if (!(loadUrls[h]?.trim() || source)) continue;
        const key = figmaHmiBundleKey(s, v, h);
        // Unity가 이 슬롯의 bytes를 현재 index key로 줬으면 그게 슬롯의 실제 이미지다. 이때 Unity는 source도
        // 같은 key로 덮어쓰므로 HEAD를 먼저 보면 Replace·삭제 뒤 같은 index의 옛 이미지가 저장된다.
        if (args.unityImageKeys.has(key)) {
          plan.unityKeys.push(key);
          continue;
        }
        // 새 썸네일을 적용하던 슬롯이다. HEAD·source는 교체 전 이미지일 수 있어 쓰지 않는다.
        if (pendingKeys.has(key)) {
          plan.deferred.push(key);
          continue;
        }
        if (FIGMA_HMI_BUNDLE_KEY_RE.test(source)) {
          // 이미지가 이전 저장 그대로인 슬롯이다. 같은 HMI(id)가 지난 Revision에서 쓰던 key를 먼저 찾고,
          // id가 없는 옛 Revision이면 슬롯에 남은 key로 찾는다. Replace는 새 id라 옛 이미지와 섞이지 않는다.
          const id = ids[h]?.trim();
          const idKey = id ? headHmiKeyById?.get(id) : undefined;
          const head = (idKey ? headByKey.get(idKey) : undefined) ?? headByKey.get(source);
          if (head) {
            plan.refs.push({ ...head, logical_key: key });
            continue;
          }
        } else if (isHttpUrl(source)) {
          const asset = await args.ensureAsset(args.projectId, source);
          if (asset) {
            plan.refs.push({ ...asset, logical_key: key });
            continue;
          }
        }
        fallback(key);
      }
    }
  }
  // 프로젝트 썸네일은 사용자 Save에서만 촬영한다. 자동 저장·늦은 렌더로 Unity가 bytes를 주지 않으면
  // 마지막 Revision ref를 유지해야 Home 카드 썸네일이 사라지지 않는다. HMI가 아닌 대기 key도 같다.
  for (const key of Array.from(new Set([PROJECT_THUMBNAIL_KEY, ...Array.from(pendingKeys)]))) {
    if (FIGMA_HMI_BUNDLE_KEY_RE.test(key) || args.unityImageKeys.has(key)) continue;
    const head = headByKey.get(key);
    if (head) plan.refs.push({ ...head });
  }
  return plan;
}

/**
 * STUDIO-PACKAGE-001 「저장 시점에 이미지가 없을 때 — 동적 판정」.
 * 종류별 고정 등급이 아니라 항목별로 재생성 근거가 살아 있으면 `regenerable`(저장 진행 + 재생성),
 * 없으면 `required`(저장 중단)다.
 */
export type ImageAssetGrade = "required" | "regenerable";

export interface ImageAssetGrading {
  key: string;
  grade: ImageAssetGrade;
  /** 재생성 근거. required이면 null. */
  basis: string | null;
}


export function gradeMissingImageKey(key: string, viewRData: ViewRData): ImageAssetGrading {
  const spaces = viewRData.spaces ?? [];
  const hmi = key.match(FIGMA_HMI_BUNDLE_KEY_RE);
  if (hmi) {
    const [s, v, h] = [Number(hmi[1]), Number(hmi[2]), Number(hmi[3])];
    const source =
      spaces[s]?.vseList?.[v]?.vseFunction?.vseHmiInfo?.hmiPreviewThumbnailUrl?.[h]?.trim() ?? "";
    return isHttpUrl(source)
      ? { key, grade: "regenerable", basis: "server_url" }
      : { key, grade: "required", basis: null };
  }
  if (COMMENT_ATTACHMENT_KEY_RE.test(key)) return { key, grade: "required", basis: null };
  if (COMMENT_PROFILE_KEY_RE.test(key)) return { key, grade: "regenerable", basis: "photo_url" };
  const capture = key.match(/^capture_([0-9a-f]{32})\./);
  if (capture) {
    for (const space of spaces) {
      const comment = space?.commentList?.find((item) => item?.id === capture[1]);
      if (comment) {
        return comment.cameraTransformData
          ? { key, grade: "regenerable", basis: "camera_transform" }
          : { key, grade: "required", basis: null };
      }
    }
    return { key, grade: "required", basis: null };
  }
  const xr = key.match(/^xr_entry_([A-Za-z0-9-]+)\.png$/);
  if (xr) {
    for (const space of spaces) {
      const entries = (space as {
        xrEntryPoints?: Array<{ entryPointId?: string; pinCameraTransform?: unknown } | null>;
      })?.xrEntryPoints;
      const entry = entries?.find((item) => item?.entryPointId?.trim() === xr[1]);
      if (entry) {
        return entry.pinCameraTransform
          ? { key, grade: "regenerable", basis: "entry_transform" }
          : { key, grade: "required", basis: null };
      }
    }
    return { key, grade: "required", basis: null };
  }
  // 프로젝트 썸네일 등: 카메라 상태에서 다시 렌더할 수 있다.
  return { key, grade: "regenerable", basis: "camera_state" };
}


/** Reuse only the exact committed inventory, with no image work still pending. */
export function canReuseRevisionImages(projectId: string): boolean {
  if (pendingByProject.get(projectId)?.size || staleByProject.get(projectId)?.size) return false;
  const refs = refsByProject.get(projectId);
  if (!refs) return false;
  for (const [key, ref] of Array.from(committedByProject.get(projectId)?.entries() ?? []))
    if (!refs.some((saved) => saved.logical_key === key && saved.source === ref.source && saved.asset_id === ref.asset_id)) return false;
  return true;
}
